import './styles.scss'
import React, { useContext, useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import DropdownComponent from 'Components/DropdownComponent'
import { popupIcon } from 'Assets/Icons'
import apiAdapterCoffeeWeb from 'Services/apiAdapter_CoffeeWeb'
import { CoffeeWebContext } from 'Context/CoffeeWebContext'
import { commonMethods } from 'Utils/commonMethods'
import { getLocalUserDetails } from 'Utils/LocalStorage_Handler'
import { useLocation } from 'react-router-dom/cjs/react-router-dom.min'

const LotWiseMobileView = (props) => {
  const { t } = useTranslation()
  const { setLoading } = useContext(CoffeeWebContext)
  const { fetchedData, reportName, setSelectedMarket, selectedMarket } = props

  const location = useLocation()
  const fullPath = location.pathname
  const pathSegments = fullPath?.split('/')
  const desiredPath = pathSegments[2] || ''
  const userDetails = getLocalUserDetails()
  let menuLock

  if (userDetails && userDetails.menus) {
    const menu = userDetails.menus.find(({ menuUrl }) => menuUrl === desiredPath)

    if (menu) {
      menuLock = menu.menulock
    }
  }

  const { checkForUserAccess, dateFormatBasedOnUser } = commonMethods

  const [label, setLabel] = useState([])
  const [expandedRow, setExpandedRow] = useState(null)
  const [spreadingData, setSpreadingData] = useState([])
  const [labelLotWise, setLabelLotWise] = useState('')
  const [errorMessage, setErrorMessage] = useState({
    isError: false,
    message: ''
  })

  useEffect(() => {
    if (fetchedData?.details) {
      const data = fetchedData.details.map((item) => item.label)

      if (!data.includes('OPEN INTEREST')) {
        data.push('OPEN INTEREST')
      }

      setLabel(data)

      if (data.length > 0) {
        setLabelLotWise(data[0])
      }
    }
  }, [fetchedData])

  const getSpreadingData = async (date, labelType) => {
    setLoading(true)
    setErrorMessage({
      ...errorMessage,
      isError: false,
      message: ''
    })
    try {
      const response = await apiAdapterCoffeeWeb.getLotWiseSpreadingData({ reportName, labelType, date })

      setSpreadingData(response.data.returnLst)
    } catch (error) {
      setErrorMessage({
        ...errorMessage,
        isError: true,
        message: t(' NO_DATA_FOUND')
      })
    } finally {
      setLoading(false)
    }
  }

  const handleCoffeeReportsChange = (selectedLabel) => {
    setLabelLotWise(selectedLabel)
    setExpandedRow(null)
  }

  const handleMarketNameChange = () => {
    setSelectedMarket(selectedMarket === 'Robusta' ? 'Arabica' : 'Robusta')
    setExpandedRow(null)
  }

  const isOpenInterestSelected = labelLotWise === 'OPEN INTEREST'
  const openInterestData = fetchedData?.terminalDetails || []

  const filteredData = isOpenInterestSelected ? openInterestData : fetchedData?.details?.find((item) => item.label === labelLotWise)

  const getLabelClass = (value) => {
    if (value > 0) {
      return 'positive-label'
    }
    if (value < 0) {
      return 'negative-label'
    }

    return 'neutral-label'
  }

  const handleRowExpandData = (index, item) => {
    const labelTypeMap = {
      'FUND BUYERS': 1,
      'SWAP DEALERS': 3,
      'OTHER REPORTABLE': 4
    }

    const data = fetchedData?.details?.find((item) => item.label === labelLotWise)
    const labelType = labelTypeMap[data?.label] || 4

    setSpreadingData([])
    setExpandedRow(expandedRow === index ? null : index)

    if (expandedRow !== index) {
      getSpreadingData(item.date, labelType)
    }
  }

  const renderOpenInterestTable = () => {
    if (openInterestData.length > 0) {
      return openInterestData.map((item, index) => (
        <tr key={index} className="open-interest-tr">
          <td>{item.date}</td>
          <td>{item.terminalLevel}</td>
          <td>{item.openInterest.toLocaleString()}</td>
          <td className={item.netChange >= 0 ? 'positive-label' : 'negative-label'}>{item.netChange.toLocaleString()}</td>
        </tr>
      ))
    }
  }

  const formatDate = (dateStr) => {
    if (!dateStr || typeof dateStr !== 'string') return '' // Fallback if date is missing or not a string

    const parts = dateStr.split('-') // ['08', 'Apr', '2025']

    if (parts.length !== 3) return dateStr // Return original if format is unexpected

    const shortYear = parts[2].slice(-2)

    return `${parts[0]}-${parts[1]}-${shortYear}`
  }

  let tableContent

  if (isOpenInterestSelected) {
    tableContent = renderOpenInterestTable()
  } else if (filteredData) {
    tableContent = (
      <>
        {/* Net CHG Row */}
        <tr className="net-chg-row">
          <th>{t('NET_CHANGES')}</th>
          <th className="positive-label">{filteredData.long?.toLocaleString()}</th>
          <th className="negative-label">{filteredData.short?.toLocaleString()}</th>
          <th>{filteredData.total?.toLocaleString()}</th>
          <th className="positive-label">{filteredData.percentLong?.toFixed(2)}</th>
          <th className="negative-label">{filteredData.percentShort?.toFixed(2)}</th>
        </tr>

        {/* Data Rows */}
        {filteredData?.data?.map((item, index) => {
          const { spreading, date, long, short, total, percentLong, percentShort } = item

          return (
            <React.Fragment key={index}>
              <tr>
                <td className={`${spreading ? 'date-column' : 'date-without-icon'}`}>
                  {formatDate(dateFormatBasedOnUser(date))}
                  {spreading !== 0 && (
                    <div className="icon-wrapper" data-testid="total-icon-container" onClick={() => handleRowExpandData(index, item)} style={{ cursor: 'pointer' }}>
                      <img src={popupIcon} alt="icon" className="popup-icon" title="total-icon-image" />
                    </div>
                  )}
                </td>
                <td className="positive-label">{long.toLocaleString()}</td>
                <td className="negative-label">{short.toLocaleString()}</td>
                <td>{total.toLocaleString()}</td>
                <td className="positive-label">{percentLong?.toFixed(2)}</td>
                <td className="negative-label">{percentShort?.toFixed(2)}</td>
              </tr>

              {/* Spreading Data */}
              {expandedRow === index && (
                <tr className="expanded-row">
                  <td colSpan={6} className="spreading-td">
                    <div className="spreading-data-div">
                      <table>
                        <thead>
                          <tr>
                            <th colSpan={3}>{t('SPREADING')}</th>
                          </tr>
                        </thead>
                        <tbody>
                          <tr>
                            <th>{t('REPORT_DATE')}</th>
                            <th>{t('LOTS')}</th>
                            <th>{t('NET_CHANGE')}</th>
                          </tr>
                          {errorMessage.isError ? (
                            <p>{errorMessage.message}</p>
                          ) : (
                            spreadingData.map((e, spreadingIndex) => (
                              <tr key={spreadingIndex}>
                                <td>{formatDate(dateFormatBasedOnUser(e?.date))}</td>
                                <td>{e.spreading}</td>
                                <td className={getLabelClass(e.compareToPreviousDay)}>{e.compareToPreviousDay}</td>
                              </tr>
                            ))
                          )}
                        </tbody>
                      </table>
                    </div>
                  </td>
                </tr>
              )}
            </React.Fragment>
          )
        })}
      </>
    )
  }

  return (
    <div className="lot-wise-mobile-view" data-testid="lot-wise-mobile-view">
      <div className="table-heading">
        <div className="market-type-dropdown" data-testid="market-type-drop-down">
          <div className="market-selector" onClick={handleMarketNameChange} data-testid="market-type-selector">
            {selectedMarket}
            <div className="arrow-toggle" data-testid="arrow-toggle">
              <i className="pi pi-angle-up arrow-up" data-testid="arrow-up-icon" />
              <i className="pi pi-angle-down arrow-down" data-testid="arrow-down-icon" />
            </div>
          </div>
        </div>
        <div className="lot-wise-mobile-ui-dropdown">
          <DropdownComponent handleSelectCountry={handleCoffeeReportsChange} countryList={label} currentItem={labelLotWise} setCurrentItem={setLabelLotWise} placeholderText={label.length ? t('SELECT_REPORT') : t('NO_DATA_FOUND')} lotWiseDropDown={true} height="41px" testingId="coffee-report-dropdown" />
        </div>
      </div>
      <table>
        <thead>
          {isOpenInterestSelected ? (
            <tr className="open-interest-thead">
              <th>{t('REPORT_DATE')}</th>
              <th>{t('TERMINAL_LEVEL_UPPERCASE')}</th>
              <th>{t('OPEN_INTEREST_UPPERCASE')}</th>
              <th>{t('NET_CHANGE_UPPERCASE')}</th>
            </tr>
          ) : (
            <>
              <tr className="top-header-tr">
                <th>{t('REPORT_DATE')}</th>
                <th className="positive-label">{t('LONG')}</th>
                <th className="negative-label">{t('SHORT')}</th>
                <th>{t('TOTAL_VALUE')}</th>
                <th className="positive-label">{t('PERCENTAGE_LONG')}</th>
                <th className="negative-label">{t('PERCENTAGE_SHORT')}</th>
              </tr>
            </>
          )}
        </thead>
        {!isOpenInterestSelected && <tr className="empty-tr" />}

        <tbody style={checkForUserAccess(menuLock)}>{tableContent}</tbody>
      </table>
    </div>
  )
}

export default LotWiseMobileView
